import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/api/fetchApi";
import { errorText } from "@/components/ErrorPanel";
import { Spinner } from "@/components/Spinner";
import { loginIdFromFlinksMessage } from "./api";

/** The backend's BANK_* errors are written for the user; anything else gets the usual text. */
export const flinksErrorText = (e: unknown) =>
  e instanceof ApiError && e.code.startsWith("BANK_") ? e.message : errorText(e);

/**
 * Errors after which the same login can't be imported again (the Connect session is spent): "Try
 * again" opens Flinks Connect anew instead of repeating the import.
 */
const START_OVER = new Set(["BANK_SESSION_EXPIRED", "BANK_LOGIN_INVALID"]);

type Phase =
  | { kind: "opening" }
  | { kind: "connect"; url: string }
  | { kind: "reading"; loginId: string }
  | { kind: "error"; message: string; loginId?: string };

/**
 * One Flinks Connect session in the check-in modal's frame (`.ciwrap`): `start` gives the iframe URL
 * (a new connection, or a reconnect on a request that needs the user); when Flinks posts its REDIRECT
 * message, `finish` reads the balances. Used by onboarding's Banking step and by Settings.
 */
export function FlinksDialog({
  title,
  start,
  finish,
  onClose,
}: {
  title: string;
  start: () => Promise<string>;
  finish: (loginId: string) => Promise<void>;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: "opening" });
  const started = useRef(false);

  const open = useCallback(async () => {
    setPhase({ kind: "opening" });
    try {
      setPhase({ kind: "connect", url: await start() });
    } catch (e) {
      setPhase({ kind: "error", message: flinksErrorText(e) });
    }
  }, [start]);

  const read = useCallback(
    async (loginId: string) => {
      setPhase({ kind: "reading", loginId });
      try {
        await finish(loginId);
      } catch (e) {
        const startOver = e instanceof ApiError && START_OVER.has(e.code);
        setPhase({
          kind: "error",
          message: flinksErrorText(e),
          loginId: startOver ? undefined : loginId,
        });
      }
    },
    [finish],
  );

  useEffect(() => {
    // Once per mount (StrictMode runs effects twice; each session costs a Flinks token).
    if (started.current) return;
    started.current = true;
    void open();
  }, [open]);

  const iframeOrigin = phase.kind === "connect" ? new URL(phase.url).origin : undefined;
  useEffect(() => {
    if (!iframeOrigin) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== iframeOrigin) return;
      const loginId = loginIdFromFlinksMessage(e.data);
      if (loginId) void read(loginId);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeOrigin, read]);

  const reading = phase.kind === "reading";
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !reading && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, reading]);

  return (
    <div
      className="ciwrap flinksdlg"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flinks-title"
      data-testid="flinks-dialog"
    >
      <div className="cimodal">
        <div className="cihead">
          <div>
            <div className="eyebrow">Through Flinks</div>
            <h2 id="flinks-title" style={{ fontSize: 20, marginTop: 3 }}>
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="cix"
            onClick={onClose}
            aria-label="Close"
            disabled={reading}
          >
            &times;
          </button>
        </div>
        {phase.kind === "opening" && <Spinner label="Opening a secure connection" />}
        {phase.kind === "connect" && (
          <iframe
            className="flinksframe"
            title="Flinks Connect"
            src={phase.url}
            data-testid="flinks-iframe"
          />
        )}
        {reading && <Spinner label="Reading your balances" />}
        {phase.kind === "error" && (
          <div data-testid="flinks-error">
            <p className="wp-form-err" role="alert">
              {phase.message}
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => (phase.loginId ? void read(phase.loginId) : void open())}
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
